import React from 'react';
import PropTypes from 'prop-types';

import './checkout-skeleton.less';

export const CHECKOUT_SKELETON_VARIANTS = {
  TICKETS: 'tickets',
  BUNDLE: 'bundle',
  MULTI_DATE: 'multi-date',
  FIXED_DATE: 'fixed-date',
  PASS_GUESTS: 'pass-guests',
  PRODUCTS: 'products',
  BASKET: 'basket',
  PAYMENT: 'payment',
  CONFIRMATION: 'confirmation',
  PROFILES: 'profiles',
};

const Shape = ({ className = '' }) => (
  <span
    className={`checkout-skeleton__shape ${className}`.trim()}
    aria-hidden="true"
  />
);

Shape.propTypes = {
  className: PropTypes.string,
};

const Card = ({ rows = 2, compact = false }) => (
  <div
    className={`checkout-skeleton__card${
      compact ? ' checkout-skeleton__card--compact' : ''
    }`}
    aria-hidden="true">
    <div className="checkout-skeleton__card-copy">
      <Shape className="checkout-skeleton__line checkout-skeleton__line--medium" />
      {Array.from({ length: rows }).map((_, index) => (
        <Shape
          key={index}
          className={`checkout-skeleton__line ${
            index === rows - 1 ? 'checkout-skeleton__line--short' : ''
          }`}
        />
      ))}
    </div>
    <Shape className="checkout-skeleton__control" />
  </div>
);

Card.propTypes = {
  rows: PropTypes.number,
  compact: PropTypes.bool,
};

const Heading = ({ subtitle = true }) => (
  <div className="checkout-skeleton__heading" aria-hidden="true">
    <Shape className="checkout-skeleton__line checkout-skeleton__line--title" />
    {subtitle && (
      <Shape className="checkout-skeleton__line checkout-skeleton__line--subtitle" />
    )}
  </div>
);

Heading.propTypes = {
  subtitle: PropTypes.bool,
};

const DateTiles = ({ count = 6 }) => (
  <div className="checkout-skeleton__date-tiles" aria-hidden="true">
    {Array.from({ length: count }).map((_, index) => (
      <Shape key={index} className="checkout-skeleton__date-tile" />
    ))}
  </div>
);

DateTiles.propTypes = {
  count: PropTypes.number,
};

const TicketBody = () => (
  <>
    <Heading />
    <Shape className="checkout-skeleton__line checkout-skeleton__line--legend" />
    <DateTiles />
    <div className="checkout-skeleton__section">
      <Shape className="checkout-skeleton__line checkout-skeleton__line--section-title" />
      <Card />
      <Card rows={1} />
    </div>
  </>
);

const BundleBody = () => (
  <>
    <Heading />
    <div className="checkout-skeleton__date-fields" aria-hidden="true">
      {[0, 1].map((index) => (
        <div className="checkout-skeleton__date-field" key={index}>
          <Shape className="checkout-skeleton__line checkout-skeleton__line--medium" />
          <Shape className="checkout-skeleton__date-trigger" />
        </div>
      ))}
    </div>
  </>
);

const MultiDateBody = () => (
  <>
    <div className="checkout-skeleton__event-card" aria-hidden="true">
      <div>
        <Shape className="checkout-skeleton__line checkout-skeleton__line--title" />
        <Shape className="checkout-skeleton__line checkout-skeleton__line--medium" />
        <Shape className="checkout-skeleton__line checkout-skeleton__line--short" />
      </div>
      <Shape className="checkout-skeleton__event-image" />
    </div>
    <Heading subtitle={false} />
    <DateTiles count={2} />
  </>
);

const GuestBody = ({ showEvent = false }) => (
  <>
    {showEvent && (
      <div className="checkout-skeleton__event-card" aria-hidden="true">
        <div>
          <Shape className="checkout-skeleton__line checkout-skeleton__line--title" />
          <Shape className="checkout-skeleton__line checkout-skeleton__line--medium" />
        </div>
        <Shape className="checkout-skeleton__event-image" />
      </div>
    )}
    <Heading subtitle={false} />
    <Card />
    <Card rows={1} />
  </>
);

GuestBody.propTypes = {
  showEvent: PropTypes.bool,
};

const ProductsBody = () => (
  <>
    <Heading />
    <div className="checkout-skeleton__product-grid">
      <Card />
      <Card />
      <Card rows={1} />
    </div>
  </>
);

const BasketBody = () => (
  <>
    <Heading subtitle={false} />
    <Card />
    <Card />
  </>
);

const PaymentBody = () => (
  <>
    <Heading />
    <div className="checkout-skeleton__form" aria-hidden="true">
      {[0, 1, 2, 3].map((index) => (
        <div key={index}>
          <Shape className="checkout-skeleton__line checkout-skeleton__line--short" />
          <Shape className="checkout-skeleton__field" />
        </div>
      ))}
    </div>
  </>
);

const ConfirmationBody = () => (
  <div className="checkout-skeleton__confirmation" aria-hidden="true">
    <Shape className="checkout-skeleton__confirmation-icon" />
    <Shape className="checkout-skeleton__line checkout-skeleton__line--title" />
    <Shape className="checkout-skeleton__line checkout-skeleton__line--subtitle" />
    <Card rows={2} />
  </div>
);

const ProfilesBody = () => (
  <>
    <Heading />
    <div className="checkout-skeleton__profile-grid">
      <Card />
      <Card />
    </div>
  </>
);

const getBody = (variant) => {
  switch (variant) {
    case CHECKOUT_SKELETON_VARIANTS.BUNDLE:
      return <BundleBody />;
    case CHECKOUT_SKELETON_VARIANTS.MULTI_DATE:
      return <MultiDateBody />;
    case CHECKOUT_SKELETON_VARIANTS.FIXED_DATE:
      return <GuestBody showEvent />;
    case CHECKOUT_SKELETON_VARIANTS.PASS_GUESTS:
      return <GuestBody />;
    case CHECKOUT_SKELETON_VARIANTS.PRODUCTS:
      return <ProductsBody />;
    case CHECKOUT_SKELETON_VARIANTS.BASKET:
      return <BasketBody />;
    case CHECKOUT_SKELETON_VARIANTS.PAYMENT:
      return <PaymentBody />;
    case CHECKOUT_SKELETON_VARIANTS.CONFIRMATION:
      return <ConfirmationBody />;
    case CHECKOUT_SKELETON_VARIANTS.PROFILES:
      return <ProfilesBody />;
    case CHECKOUT_SKELETON_VARIANTS.TICKETS:
    default:
      return <TicketBody />;
  }
};

export const getCheckoutSkeletonVariant = (pathname = '') => {
  const path = pathname.toLowerCase();

  if (path === '/cart/completed' || path.includes('payment-confirmed'))
    return CHECKOUT_SKELETON_VARIANTS.CONFIRMATION;
  if (path === '/cart/checkout' || /\/birthday\/[^/]+\/payment\/?$/.test(path))
    return CHECKOUT_SKELETON_VARIANTS.PAYMENT;
  if (path === '/cart') return CHECKOUT_SKELETON_VARIANTS.BASKET;
  if (path.includes('/season-profiles'))
    return CHECKOUT_SKELETON_VARIANTS.PROFILES;
  if (path.includes('/bundle/') && /\/(addons|seating|upgrades)\/?$/.test(path))
    return CHECKOUT_SKELETON_VARIANTS.PRODUCTS;
  if (path.startsWith('/bundle/')) return CHECKOUT_SKELETON_VARIANTS.BUNDLE;
  if (/\/(addons|upgrades)\/?$/.test(path))
    return CHECKOUT_SKELETON_VARIANTS.PRODUCTS;

  return CHECKOUT_SKELETON_VARIANTS.TICKETS;
};

export const isCheckoutPath = (pathname = '') => {
  const path = pathname.toLowerCase();

  return (
    path === '/cart' ||
    path.startsWith('/cart/') ||
    path.startsWith('/bundle/') ||
    path.startsWith('/birthday/') ||
    /\/(venue|promo)\/[^/]+\/(tickets|addons|upgrades|season-profiles)/.test(
      path
    )
  );
};

const CheckoutSkeleton = ({ variant = CHECKOUT_SKELETON_VARIANTS.TICKETS }) => (
  <div
    className={`checkout-skeleton checkout-skeleton--${variant}`}
    data-skeleton-variant={variant}
    aria-busy={true}
    aria-live="polite"
    role="status">
    <span className="checkout-skeleton__status">Loading checkout</span>
    <div className="checkout-skeleton__step-bar" aria-hidden="true">
      <div className="checkout-skeleton__progress" />
      <div className="checkout-skeleton__step-label">
        <Shape className="checkout-skeleton__line checkout-skeleton__line--step" />
      </div>
    </div>
    <div className="checkout-skeleton__wrapper">
      <div className="checkout-skeleton__container">
        <section className="checkout-skeleton__content">
          {getBody(variant)}
        </section>
        {variant !== CHECKOUT_SKELETON_VARIANTS.CONFIRMATION && (
          <aside className="checkout-skeleton__summary" aria-hidden="true">
            <Shape className="checkout-skeleton__line checkout-skeleton__line--medium" />
            <Shape className="checkout-skeleton__summary-image" />
            <Shape className="checkout-skeleton__line" />
            <Shape className="checkout-skeleton__line checkout-skeleton__line--short" />
            <div className="checkout-skeleton__summary-footer">
              <Shape className="checkout-skeleton__line checkout-skeleton__line--medium" />
              <Shape className="checkout-skeleton__button" />
            </div>
          </aside>
        )}
      </div>
    </div>
  </div>
);

CheckoutSkeleton.propTypes = {
  variant: PropTypes.oneOf(Object.values(CHECKOUT_SKELETON_VARIANTS)),
};

export const CheckoutRouteSkeleton = ({ pathname }) => {
  const currentPath =
    pathname || (typeof window !== 'undefined' ? window.location.pathname : '');

  if (!isCheckoutPath(currentPath)) return null;

  return <CheckoutSkeleton variant={getCheckoutSkeletonVariant(currentPath)} />;
};

CheckoutRouteSkeleton.propTypes = {
  pathname: PropTypes.string,
};

export default CheckoutSkeleton;
